"use client";

import dynamic from "next/dynamic";
import { useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useSiteSearch } from "react-datocms";
import { buildClient } from "@datocms/cma-client-browser";

import { Loader } from "components/atoms/Loader/Loader";
import { Spacer } from "components/organisms/Spacer/Spacer";
import { SearchForm } from "components/organisms/Form/SearchForm";
import { BE_LOCALES, Locale } from "consts/locale";

const SearchResults = dynamic(
  () =>
    import("components/organisms/SearchResults/SearchResults").then((module) => ({
      default: module.SearchResults,
    })),
  { loading: () => <Loader /> },
);

interface Props {
  locale: Locale;
}

export function SearchPageClient({ locale }: Props) {
  const searchParams = useSearchParams();
  const termFromUrl = searchParams.get("term") ?? searchParams.get("q") ?? "";

  const client = useMemo(
    () =>
      buildClient({
        apiToken: `${process.env.NEXT_PUBLIC_DATOCMS_API_TOKEN}`,
      }),
    [],
  );

  const { state, error, data } = useSiteSearch({
    client: client as any,
    initialState: { locale, query: termFromUrl },
    searchIndexId: BE_LOCALES.includes(locale)
      ? process.env.NEXT_PUBLIC_DATOCMS_SEARCH_INDEX_ID_BE
      : process.env.NEXT_PUBLIC_DATOCMS_SEARCH_INDEX_ID_LU,
    resultsPerPage: 10,
  });

  return (
    <Spacer size="s">
      <SearchForm
        defaultSearchValue={termFromUrl}
        onSubmit={(values) => {
          state.setQuery(values.search);
        }}
      />
      {data && <SearchResults state={{ ...state, locale }} data={data} error={error} />}
    </Spacer>
  );
}
